<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">Floors and limits</h1>
				<p class="cs-sub">Our cost, the floor and the max discount for every price. Once the sales lever is on, sales can discount a line down to its floor and by no more than the max discount without asking you. A price with no floor sends every discount on it to you. List prices are not changed here.</p>
			</div>
			<div class="row"><button class="pill" :class="{ on: guideOpen }" @click="guideOpen = !guideOpen"><i class="fa-regular fa-circle-question"></i> What counts as our cost</button></div>
		</div>

		<div v-if="guideOpen" class="qblock" style="margin:0 0 14px">
			<h5>What counts as our cost <span class="sub">· what to add up for one client for one year, or once for a one-time item</span></h5>
			<ul class="pe-guide">
				<li><b>Features we host and have already built,</b> such as AI search, alerts, subscriptions or online forms: the share of hosting and usage the client causes, such as AI requests, text messages and emails sent. Product and engineering have the usage numbers.</li>
				<li><b>Builds and design work,</b> such as the Themes, Premier and Elite builds or a redesign: the hours of the people who build it times their hourly cost. Theme builds are on the delivery timesheets.</li>
				<li><b>Support and White Glove:</b> the people time one client takes in a year.</li>
				<li><b>What we buy from others,</b> such as domain registration: what the supplier charges us.</li>
				<li><b>The floor</b> is our cost plus overhead plus the minimum margin you want. <b>The max discount</b> caps the percent sales can take off the list price.</li>
				<li><b>One value for every size.</b> When the cost does not change with the population, keep one row for the SKU. Open it per size only where the cost does change.</li>
			</ul>
			<p class="sub-hint">Development and product time is not tracked yet, so a cost that depends on it needs that tracking first. Leave the cost empty until then; empty is never read as zero.</p>
		</div>

		<pe-views :views="scheduleViews" :active-key="scheduleId" :can-save="false" @select="pick($event.key)" />

		<div v-if="schedule" class="pe-fin-bar">
			<span v-if="schedule.draft"><b>Draft v{{ schedule.draft.version_no }}</b> holds your saved changes. Quotes keep using v{{ schedule.published.version_no }} until you publish.</span>
			<span v-else-if="schedule.published">Published v{{ schedule.published.version_no }} on {{ fmtDate(schedule.published.published_at) }}. Saving puts your changes in draft v{{ schedule.published.version_no + 1 }}; quotes use them once you publish.</span>
			<span class="sp">
				<span v-if="changed.length" class="yn warn" style="align-self:center">{{ changed.length }} not saved</span>
				<button class="pill sm" :disabled="!changed.length || !!busy" @click="discard">Discard</button>
				<button class="pill sm" :disabled="!changed.length || !!busy || invalid > 0" :title="invalid ? 'Fix the rows marked in the Check column first' : ''" @click="save">{{ busy === 'save' ? 'Saving…' : 'Save draft' }}</button>
				<button class="btn-primary" :disabled="!schedule.draft || changed.length > 0 || !!busy" :title="changed.length ? 'Save or discard the changes first' : ''" @click="publish">{{ schedule.draft ? 'Publish v' + schedule.draft.version_no : 'Publish' }}</button>
			</span>
		</div>

		<pe-toolbar v-model="search" placeholder="Search a SKU or name" :pills="pills" :active-pills="[filter]" :show-filters="false" :show-columns="false" style="margin-top:12px" @pill="filter = $event" />

		<div class="tbl-scroll">
			<table class="tbl nochk pe-fin-sheet">
				<thead><tr><th class="sticky-l2">Population</th><th class="r">List price</th><th class="r">Our cost $</th><th class="r">Floor $</th><th class="r">Max discount %</th><th>Check</th></tr></thead>
				<tbody v-if="!loading">
					<template v-for="g in groups">
						<tr :key="'g' + g.code" class="grp"><td colspan="6">{{ g.name }} <span class="mono" style="text-transform:none;letter-spacing:0;font-weight:500">{{ g.code }}</span>
							<a v-if="g.rows.length > 1 && !isPerSize(g)" href="#" class="pe-fin-copy" @click.prevent="$set(perSize, g.code, true)">set per size</a>
							<a v-else-if="g.rows.length > 1" href="#" class="pe-fin-copy" @click.prevent="oneForAll(g)">one value for every size</a></td></tr>
						<tr v-if="g.rows.length > 1 && !isPerSize(g)" :key="'all' + g.code">
							<td class="sticky-l2">every size <span class="faint">· {{ g.rows.length }} population bands</span></td>
							<td class="r num">{{ listRange(g) }}</td>
							<td v-for="f in ['cost', 'floor', 'cap']" :key="f" class="inp"><input type="number" min="0" :max="f === 'cap' ? 100 : null" :step="f === 'cap' ? 0.5 : 0.01" placeholder="not set" :value="edits[g.rows[0].key][f]" :class="{ changed: g.rows.some(r => isChanged(r, f)) }" :aria-label="{ cost: 'Our cost', floor: 'Floor', cap: 'Max discount' }[f] + ', ' + g.code + ', every size'" @input="setAll(g, f, $event.target.value)" /></td>
							<td :class="groupCheck(g).tone === 'warn' ? 'warn' : 'faint'">{{ groupCheck(g).text }}</td>
						</tr>
						<tr v-for="r in (g.rows.length > 1 && !isPerSize(g) ? [] : g.rows)" :key="r.key">
							<td class="sticky-l2">{{ r.band_min !== null ? bandLabel(r.band_min, r.band_max) + ' residents' : (r.formula_json ? 'every size, by formula' : 'any size') }}</td>
							<td class="r num">{{ r.formula_json ? formulaText(r.formula_json, true) || 'formula' : money(r.price_cents) }}</td>
							<td class="inp"><input type="number" min="0" step="0.01" placeholder="not set" v-model="edits[r.key].cost" :class="{ changed: isChanged(r, 'cost') }" :aria-label="'Our cost, ' + g.code" /></td>
							<td class="inp"><input type="number" min="0" step="0.01" placeholder="not set" v-model="edits[r.key].floor" :class="{ changed: isChanged(r, 'floor') }" :aria-label="'Floor, ' + g.code" /></td>
							<td class="inp"><input type="number" min="0" max="100" step="0.5" placeholder="not set" v-model="edits[r.key].cap" :class="{ changed: isChanged(r, 'cap') }" :aria-label="'Max discount, ' + g.code" /></td>
							<td :class="check(r).tone === 'warn' ? 'warn' : 'faint'">{{ check(r).text }}</td>
						</tr>
					</template>
					<tr v-if="!groups.length" class="empty"><td colspan="6"><i class="fa-regular fa-folder-open"></i><div>No price matches.</div></td></tr>
				</tbody>
				<tbody v-else><tr v-for="n in 6" :key="n" class="skeleton"><td colspan="6"><span class="sk"></span></td></tr></tbody>
			</table>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api/index.js'
import { PeViews, PeToolbar } from '../../../components/pe'
import { money, fmtDate, bandLabel, formulaText, loadLocal, saveLocal, errorText } from '../pe-util'

const KEY = 'pricing-floors-schedule'
const dollars = c => (c === null || c === undefined ? '' : String(c / 100))
const toCents = v => (v === '' || v === null || v === undefined ? null : Math.round(Number(v) * 100))

export default {
	name: 'PricingFloors',
	components: { PeViews, PeToolbar },
	data() {
		return { schedules: [], scheduleId: loadLocal(KEY, null), rows: [], edits: {}, versionId: null, loading: false, busy: '', search: '', filter: 'all', perSize: {}, guideOpen: false }
	},
	computed: {
		schedule() { return this.schedules.find(s => s.id === this.scheduleId) || null },
		scheduleViews() { return this.schedules.map(s => ({ key: s.id, name: s.name + (s.draft ? ' · draft' : ''), help: s.code })) },
		pills() {
			const n = f => this.rows.filter(f).length
			return [{ value: 'all', label: 'All prices', count: this.rows.length, tone: 'grey' }, { value: 'missing', label: 'No floor yet', count: n(r => r.floor_cents === null), tone: 'amber' }, { value: 'set', label: 'Floor set', count: n(r => r.floor_cents !== null), tone: 'green' }]
		},
		groups() {
			const q = this.search.trim().toLowerCase()
			const keep = { all: () => true, missing: r => r.floor_cents === null, set: r => r.floor_cents !== null }[this.filter] || (() => true)
			const out = {}
			this.rows.filter(r => keep(r) && (!q || r.sku.code.toLowerCase().includes(q) || r.sku.name.toLowerCase().includes(q))).forEach(r => { (out[r.sku.code] = out[r.sku.code] || { code: r.sku.code, name: r.sku.name, rows: [] }).rows.push(r) })
			return Object.values(out).sort((a, b) => a.code.localeCompare(b.code))
		},
		changed() { return this.rows.filter(r => ['cost', 'floor', 'cap'].some(f => this.isChanged(r, f))) },
		invalid() { return this.rows.filter(r => this.check(r).bad).length },
	},
	created() { this.loadSchedules() },
	beforeRouteLeave(to, from, next) {
		if (!this.changed.length) return next()
		this.$root.$pePrompt({ title: 'Leave without saving?', text: this.changed.length + (this.changed.length === 1 ? ' change is' : ' changes are') + ' not saved.', confirm: 'Leave', danger: true }).then(ok => next(!!ok))
	},
	methods: {
		money, fmtDate, bandLabel, formulaText,
		toast(title, icon = 'error') { this.$root.$emit('pe-notice', { tone: icon === 'success' ? 'ok' : (icon === 'info' ? 'info' : 'warn'), text: title }) },
		loadSchedules() {
			return restApi.get('/pricing/schedules').then(({ data }) => {
				this.schedules = data.filter(s => s.published || s.draft)
				if (!this.schedule && this.schedules.length) this.scheduleId = this.schedules[0].id
				return this.loadRows()
			}).catch(err => this.toast(errorText(err, 'Could not load the price lists')))
		},
		loadRows() {
			const s = this.schedule
			if (!s) return Promise.resolve()
			this.loading = true
			this.versionId = (s.draft || s.published).id
			return restApi.get('/pricing/versions/' + this.versionId + '/prices').then(({ data }) => {
				const edits = {}
				this.rows = data.prices.map(p => Object.assign(p, { key: p.sku_id + ':' + p.band_min }))
				this.rows.forEach(r => { edits[r.key] = this.original(r) })
				this.edits = edits
			}).catch(err => this.toast(errorText(err, 'Could not load the prices'))).finally(() => { this.loading = false })
		},
		pick(id) {
			if (id === this.scheduleId) return
			const go = () => { this.scheduleId = id; saveLocal(KEY, id); this.loadRows() }
			if (!this.changed.length) return go()
			this.$root.$pePrompt({ title: 'Switch without saving?', text: this.changed.length + (this.changed.length === 1 ? ' change is' : ' changes are') + ' not saved.', confirm: 'Switch', danger: true }).then(ok => { if (ok) go() })
		},
		original(r) { return { cost: dollars(r.cost_cents), floor: dollars(r.floor_cents), cap: r.max_discount_bp === null ? '' : String(r.max_discount_bp / 100) } },
		// cents and basis points are both the typed number times 100: 300 and 300.00 are the same value
		isChanged(r, f) { const e = this.edits[r.key]; const n = v => (v === '' || v === null || v === undefined ? null : Math.round(Number(v) * 100)); return !!e && n(e[f]) !== n(this.original(r)[f]) },
		check(r) {
			const e = this.edits[r.key] || {}
			const cost = toCents(e.cost), floor = toCents(e.floor), cap = e.cap === '' ? null : Number(e.cap)
			if ([cost, floor].some(v => v !== null && (isNaN(v) || v < 0)) || (cap !== null && (isNaN(cap) || cap < 0 || cap > 100))) return { tone: 'warn', bad: true, text: 'Not a valid number' }
			if (floor !== null && !r.formula_json && r.price_cents !== null && floor > r.price_cents) return { tone: 'warn', bad: true, text: 'Floor above the list price' }
			if (floor !== null && cost !== null && cost > floor) return { tone: 'warn', text: 'Floor below our cost' }
			if (floor === null) return { tone: 'faint', text: 'No floor: discounts go to finance' }
			return { tone: 'faint', text: cap === null ? 'Floor set, no max discount' : 'Set' }
		},
		/** A SKU shows one row for every size until its saved values differ by size or someone opens it per size. */
		isPerSize(g) {
			if (this.perSize[g.code] !== undefined) return this.perSize[g.code]
			const o = g.rows.map(r => JSON.stringify(this.original(r)))
			return o.some(x => x !== o[0])
		},
		setAll(g, f, v) { g.rows.forEach(r => { this.edits[r.key][f] = v }) },
		oneForAll(g) { const first = this.edits[g.rows[0].key]; g.rows.slice(1).forEach(r => { this.edits[r.key] = { cost: first.cost, floor: first.floor, cap: first.cap } }); this.$set(this.perSize, g.code, false) },
		listRange(g) {
			if (g.rows[0].formula_json) return formulaText(g.rows[0].formula_json, true) || 'formula'
			const p = g.rows.map(r => r.price_cents).filter(x => x !== null)
			if (!p.length) return 'no price'
			const lo = Math.min(...p), hi = Math.max(...p)
			return lo === hi ? money(lo) : money(lo) + ' to ' + money(hi)
		},
		/** The worst check of the SKU's sizes, with the size it is about. */
		groupCheck(g) {
			const bad = g.rows.map(r => ({ r, c: this.check(r) })).find(x => x.c.bad || x.c.tone === 'warn')
			if (!bad) return this.check(g.rows[0])
			return Object.assign({}, bad.c, { text: (bad.r.band_min !== null ? 'At ' + bandLabel(bad.r.band_min, bad.r.band_max) + ': ' : '') + bad.c.text.charAt(0).toLowerCase() + bad.c.text.slice(1) })
		},
		discard() { const edits = {}; this.rows.forEach(r => { edits[r.key] = this.original(r) }); this.edits = edits },
		async save() {
			const s = this.schedule
			this.busy = 'save'
			try {
				let draft = s.draft
				let ids = {}
				if (!draft) {
					draft = (await restApi.post('/pricing/schedules/' + s.id + '/draft')).data
					;(await restApi.get('/pricing/versions/' + draft.id + '/prices')).data.prices.forEach(p => { ids[p.sku_id + ':' + p.band_min] = p.id })
				} else this.rows.forEach(r => { ids[r.key] = r.id })
				const rows = this.changed.map(r => { const e = this.edits[r.key]; return { id: ids[r.key], cost_cents: toCents(e.cost), floor_cents: toCents(e.floor), max_discount_bp: e.cap === '' ? null : Math.round(Number(e.cap) * 100) } })
				await restApi.put('/pricing/versions/' + draft.id + '/prices', { rows })
				this.toast(rows.length + (rows.length === 1 ? ' price' : ' prices') + ' saved in draft v' + draft.version_no, 'success')
				await this.loadSchedules()
			} catch (err) { this.toast(errorText(err, 'Save failed')) } finally { this.busy = '' }
		},
		publish() {
			const d = this.schedule.draft
			this.$root.$pePrompt({ title: 'Publish v' + d.version_no + ' of ' + this.schedule.name, input: 'textarea', label: 'What changed, in one line. It shows in the price history.', confirm: 'Publish' }).then(r => {
				if (!r) return
				this.busy = 'publish'
				restApi.post('/pricing/versions/' + d.id + '/publish', { change_note: r }).then(() => { this.toast('v' + d.version_no + ' published. New quotes use it.', 'success'); return this.loadSchedules() })
					.catch(err => this.toast(errorText(err, 'Publish failed'))).finally(() => { this.busy = '' })
			})
		},
	},
}
</script>
